import React, { useEffect, useState } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import summaryStopped from "../../../../images/summary_stop.svg";
import noSummary from "../../../../images/red_no_summary.svg";
import { AIMS_STATUS, ROLES } from "../../../../constants";
import LogManager from "../../../../utils/logManager";
import { THEME_COLOR } from "../../../../styles/colors";

/**
 * Renders a generic summary-stopped screen based on AiMS status.
 * Displays different messages and visuals for NOT_AVAILABLE, SUMMARY_TO_ORGANIZER, and AVAILABLE.
 *
 * @param {Object} props
 * @param {string} props.aimsStatus - One of AIMS_STATUS.NOT_AVAILABLE | SUMMARY_TO_ORGANIZER | AVAILABLE
 * @param {string} props.role - The role of the current user
 * @param {Object} props.summaryReviewer - Summary reviewer data from API
 * @param {string} props.alias - Current user's alias
 * @param {string} props.rtmsRequesterName - Name of the person who requested the summary
 */
const SummarizeStopped = ({ aimsStatus: initAimsStatus, role, summaryReviewers, alias, rtmsRequesterName }) => {
  const [aimsStatus, setAimsStatus] = useState(initAimsStatus);

  useEffect(() => {
  }, []);

  if (aimsStatus === AIMS_STATUS.AVAILABLE) {
    // Check summary reviewer data to determine what message to show
    if (summaryReviewers && Object.keys(summaryReviewers).length > 0) {
      // Reviewers list is not empty - check if current user is in it
      const isCurrentUserReviewer = alias in summaryReviewers;
      
      LogManager.info(`Current user ${alias} is ${isCurrentUserReviewer ? '' : 'not '}in reviewers list - showing SummaryToReviewersOnly`);
      return <SummaryToReviewersOnly isCurrentUserReviewer={isCurrentUserReviewer} reviewers={summaryReviewers} />;
    }
    
    // Reviewers is empty or no reviewer data - everyone can see (normal message)
    LogManager.info("No reviewers or empty reviewers list - showing normal available message");
    return <SummaryAvailable />;
  }
  if (aimsStatus === AIMS_STATUS.NOT_AVAILABLE) return <NoSummary />;
  if (aimsStatus === AIMS_STATUS.SUMMARY_TO_ORGANIZER) return <SummaryToOrganizerOnly role={role} />;
  if (aimsStatus === AIMS_STATUS.EXTERNAL_ATTENDEE) return <MeetingHasExternalAttendee />;

  return null;
};

const SummaryAvailable = () => (
  <StoppedTemplate
    image={summaryStopped}
    header="Summarizing meeting stopped"
    message="You'll receive a meeting summary when it's ready."
  />
);

//TODO: integrate with backend
const NoSummary = () => (
  <StoppedTemplate
    image={noSummary}
    header="No meeting summary"
    message={
      <>
        There is no meeting summary because the meeting topics are not approved for Amazon Meetings Summary.
        <br />
        <br />
        Review{" "}
        <a
          href="https://w.amazon.com/bin/view/UnifiedCommunications/MeetingSummaries/AiMS"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: THEME_COLOR.MUTED_TEAL, textDecoration: "underline" }}
        >
          Amazon policy
        </a>{" "}
        for meeting recordings, transcriptions, and AI summarization.
      </>
    }
  />
);

const SummaryToOrganizerOnly = ({ role }) => (
  <StoppedTemplate
    image={summaryStopped}
    header="Summarizing meeting stopped"
    message={role === ROLES.ORGANIZER 
      ? "Only you will receive the meeting summary when it's ready."
      : `Contact meeting organizer if you want to see the meeting summary.`
    }
  />
);

const SummaryToReviewersOnly = ({ isCurrentUserReviewer, reviewers }) => {
  // Convert reviewers object to a comma-separated string of names
  const reviewerNames = reviewers ? Object.values(reviewers).join(", ") : "";
  
  return (
    <StoppedTemplate
      image={summaryStopped}
      header="Summarizing meeting stopped"
      message={isCurrentUserReviewer 
        ? "Only you will receive the meeting summary when it's ready."
        : `Contact meeting organizer, ${reviewerNames}, if you want to see the meeting summary.`
      }
    />
  );
};

//TODO: integration with backend
const MeetingHasExternalAttendee = () => (
  <StoppedTemplate
    image={summaryStopped}
    header="Summarizing meeting stopped"
    message={
      <>
        You'll receive a meeting summary when it's ready.
        <br />
        <br />
        External participants won't receive a meeting summary. Share the meeting summary with them when appropriate.
      </>
    }
  />
);

const StoppedTemplate = ({ image, header, message }) => (
  <Column alignmentHorizontal="start" width="100%" position="relative" spacing="none" spacingInset="none">
    <Row spacing="none" spacingInset="none">
      <Column width="15px" />
      <img src={image} alt="" />
    </Row>
    <Column height="16px" spacing="none" />
    <Column spacing="none">
      <Row spacing="none" spacingInset="none">
        <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.WHITE }}>
          <Box type="outline" spacingInset="none" minWidth="68px" />
        </Theme>
        <Text type="h50" alignment="start">
          {header}
        </Text>
      </Row>
    </Column>
    <Column height="16px" spacing="none" />
    <Column spacing="none" spacingInset="none">
      <Row spacing="none" spacingInset="none">
        <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.WHITE }}>
          <Box type="outline" spacingInset="none" minWidth="68px" />
        </Theme>
        <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.WHITE }}>
          <Box type="outline" spacingInset="none" maxWidth="239px">
            <Theme tokens={{ textListSpacingVertical: "160px" }}>
              <Text type="b200" alignment="start" color="secondary">
                {message}
              </Text>
            </Theme>
          </Box>
        </Theme>
      </Row>
    </Column>
  </Column>
);

export default SummarizeStopped;
